/**
 * LIFF App Container
 * Wraps the LIFF application with platform-specific behavior
 * Updated to use Cloudflare LINE LIFF Central Service SDK
 *
 * URL Parameter Routing:
 * - ?path=/good/{productId} - Auto-redirect to specified path
 * - ?view=compact - Override view type (compact/tall/full)
 *
 * Flow:
 * 1. LIFF URL: https://liff.line.me/{liffId}?path=/good/{id}&view=compact
 * 2. LIFF redirects to: https://domain.com/liff?path=/good/{id}&view=compact
 * 3. LiffApp parses parameters and redirects to: https://domain.com/good/{id}
 * 4. LiffProductPage displays in compact view
 */

import { useEffect, useState, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { useLiff } from '@misterlex223/cloudflare-line-liff-sdk/react';
import { useLiffConfig } from '../../providers/LiffProvider';
import './LiffApp.css';

const LiffApp: React.FC = () => {
  const config = useLiffConfig();
  const [viewType, setViewType] = useState<'compact' | 'tall' | 'full' | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isInClient, setIsInClient] = useState<boolean | null>(null);
  const [initError, setInitError] = useState<Error | null>(null);
  const [countdown, setCountdown] = useState(3);
  const hasInitialized = useRef(false);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Initialize LIFF SDK
  const liff = useLiff({
    ...config,
    onInitSuccess: () => {
      console.log('[LiffApp] LIFF initialized successfully');

      // 檢查是否在 LINE App 中
      const inClient = liff.isInClient();
      setIsInClient(inClient);

      if (inClient) {
        // Get context to determine view type
        const context = liff.getContext();
        const detectedViewType: 'compact' | 'tall' | 'full' =
          context.viewType === 'compact' ? 'compact' :
          context.viewType === 'tall' ? 'tall' : 'full';

        setViewType(detectedViewType);

        // Set document title
        document.title = '溫柔小姊姊｜選物 x 微批發';

        // Add platform classes to body
        document.body.classList.add('is-liff');
        document.body.classList.add(`liff-view-${detectedViewType}`);

        // Parse URL parameters for custom routing
        const urlParams = new URLSearchParams(window.location.search);
        const pathParam = urlParams.get('path');

        // Handle custom path navigation
        if (pathParam) {
          console.log('[LiffApp] Redirecting to custom path:', pathParam);
          // Redirect to the specified path
          window.location.href = window.location.origin + pathParam;
          return; // Don't set isReady to true, we're redirecting
        }
      }

      setIsReady(true);
    },
    onInitError: (error) => {
      console.error('[LiffApp] LIFF initialization failed:', error);
      setInitError(error);
      setIsReady(true);
    },
  });

  useEffect(() => {
    // Initialize LIFF when component mounts (only once)
    const initLiff = async () => {
      if (hasInitialized.current) return;
      hasInitialized.current = true;

      try {
        if (!liff.isInitialized && !liff.isLoading) {
          await liff.init();
        }
      } catch (error) {
        console.error('[LiffApp] LIFF init error:', error);
        setInitError(error instanceof Error ? error : new Error('LIFF initialization failed'));
        setIsReady(true);
      }
    };

    initLiff();
  }, []);

  // 倒數計時器
  useEffect(() => {
    if (isReady && isInClient === false && countdown > 0) {
      countdownRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownRef.current!);
            // 倒數結束，重定向到首頁
            window.location.href = '/';
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        if (countdownRef.current) {
          clearInterval(countdownRef.current);
        }
      };
    }
  }, [isReady, isInClient, countdown]);

  // 載入中
  if (!isReady) {
    return (
      <div className="liff-app-loading">
        <div className="liff-app-loading__spinner"></div>
        <p>載入中...</p>
      </div>
    );
  }

  // 不在 LINE App 中或初始化失敗 - 顯示降級頁面
  if (isInClient === false || initError) {
    return (
      <div className="liff-degrade-page">
        <div className="liff-degrade-page__content">
          <div className="liff-degrade-page__icon">📱</div>
          <h1 className="liff-degrade-page__title">請在 LINE App 中開啟</h1>
          <p className="liff-degrade-page__message">
            {initError ? 'LIFF 初始化失敗' : '此功能需要在 LINE App 中使用'}
          </p>
          <p className="liff-degrade-page__countdown">
            將在 {countdown} 秒後返回首頁...
          </p>
          <button
            className="liff-degrade-page__button"
            onClick={() => (window.location.href = '/')}
          >
            立即返回
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`liff-app liff-view-${viewType || 'full'}`}>
      {/* LIFF Safe Area Top */}
      <div className="liff-safe-area-top" />

      {/* Main Content */}
      <main className="liff-main">
        <Outlet />
      </main>

      {/* LIFF Safe Area Bottom */}
      <div className="liff-safe-area-bottom" />
    </div>
  );
};

export default LiffApp;
